/**
 * `/` Home (UX_EXPERIENCE §4.3a, #442, #576). One clear next step, what is due, and little else. Sits above the course
 * focus and is never filtered by it: every selected course's items are here, so a sheet due tomorrow in another course
 * is never hidden.
 *
 * It reports; it never assigns work and never notifies. No streak, no praise, no count, score or percentage about what
 * the learner knows - the usage meter below is a registered proportion
 * (`budget-used`) and is about money (history §12, §19).
 *
 * Not rendered, because the feature does not exist and nothing is invented:
 * - the design board's "Practice / quick check" entry (#558);
 * - the design board's "Stuck? Ask DaVinci" entry (#559);
 * - the per-course line on where the weak points are (needs the weak-point record, #440).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useCourseFocus, type FocusCourse } from '../../app/course-focus.js';
import { CourseSymbol } from '../../app/course-symbol.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { courseShortName } from '../../app/course-names.js';
import { defineMessages } from '../../i18n/messages.js';
import { assessmentDate, useAssessmentLabels } from '../setup/assessment-labels.js';
import { UsageMeter } from '../usage/usage-meter.js';
import { morphName } from '../../app/page-morph.js';

const messages = defineMessages({
  en: {
    heading: 'Home',
    nextStep: 'Your next step',
    nothingDueAll: 'Nothing is due in your courses.',
    toHomework: 'Open homework',
    toPrep: 'Open exam preparation',
    postIt: 'Post it',
    postVerb: 'Post',
    dueWhen: (when: 'today' | 'tomorrow' | null, date: string, time: string): string =>
      `due ${when === null ? '' : `${when}, `}${date}, ${time}`,
    thisWeek: 'This week',
    nothingThisDay: 'Nothing due',
    dueSoon: 'Due in the next two weeks',
    legendAria: 'Three steps: posted, checked, fixed',
    actionPost: 'Post',
    actionFeedback: 'Feedback',
    actionPrepare: 'Prepare',
    actionOpen: 'Open',
    assessmentWord: 'Assessment',
    moodleNudge: 'Connect your Moodle to see your courses',
    moodleNudgeLink: 'Connect Moodle',
    addAssessment: 'Add an assessment',
    stepPosted: 'Posted',
    stepChecked: 'Checked',
    stepFixed: 'Fixed',
  },
  he: {
    heading: 'בית',
    nextStep: 'הצעד הבא',
    nothingDueAll: 'אין הגשות בקורסים שלך.',
    toHomework: 'לשיעורי הבית',
    toPrep: 'להכנה למבחן',
    postIt: 'להעלאה',
    postVerb: 'להעלות את',
    dueWhen: (when: 'today' | 'tomorrow' | null, date: string, time: string): string =>
      `להגשה ${when === null ? '' : `${when === 'today' ? 'היום' : 'מחר'}, `}${date}, ${time}`,
    thisWeek: 'השבוע',
    nothingThisDay: 'אין הגשות',
    dueSoon: 'להגשה בשבועיים הקרובים',
    legendAria: 'שלושה שלבים: הועלה, נבדק, תוקן',
    actionPost: 'להעלאה',
    actionFeedback: 'למשוב',
    actionPrepare: 'להתכונן',
    actionOpen: 'לפתוח',
    assessmentWord: 'מבחן',
    moodleNudge: 'כדאי לחבר את המודל כדי לראות את הקורסים',
    moodleNudgeLink: 'לחיבור המודל',
    addAssessment: 'להוספת מבחן',
    stepPosted: 'פורסם',
    stepChecked: 'נבדק',
    stepFixed: 'תוקן',
  },
});

const STEP_OF = { not_posted: 0, posted: 1, checked: 2, fixed: 3 } as const;

/** One thing that is due: a homework sheet or a learner-declared assessment. */
interface DueItem {
  readonly key: string;
  /** Homework from the exercise list: `<courseRef>/<exerciseId>`, the id the Homework page's sheet cards carry. */
  readonly sheetId?: string;
  readonly course: FocusCourse;
  readonly title: string;
  readonly kind: 'homework' | 'assessment';
  /** Local calendar day, `YYYY-MM-DD`. */
  readonly day: string;
  /** Sort key (ms). An assessment has a date only, so it sorts at the start of its day. */
  readonly at: number;
  /** Homework: its due instant. */
  readonly dueAt?: string;
  /** Assessment: its date and kind label. */
  readonly date?: string;
  readonly kindLabel?: string;
  /** Homework only; absent when only the course's `nextDue` is known (that record carries no sheet state). */
  readonly stateText?: string;
  readonly state?: keyof typeof STEP_OF;
  readonly step?: number;
  /** Still to do: not yet posted or not yet checked. An unknown state counts as still to do. */
  readonly todo: boolean;
}

function dayKey(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function startOfDay(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function HomePage(): React.JSX.Element {
  const t = useMessages(messages);
  const labels = useHomeworkLabels();
  const { locale } = useLocale();
  const api = useApi();
  const { selectedCourses, setFocus } = useCourseFocus();
  const exercises = useQuery({ queryKey: queryKeys.homeworkExercises, queryFn: () => api.homeworkExercises() });
  const assessments = useQuery({ queryKey: queryKeys.assessments, queryFn: () => api.getAssessments() });
  const kinds = useAssessmentLabels();
  // #497: one small nudge, never a gate. Shown only on a definite "not connected".
  const moodle = useQuery({ queryKey: queryKeys.moodleConnection, queryFn: () => api.getMoodleConnection() });
  const [picked, setPicked] = useState(0);

  const intl = locale === 'he' ? 'he-IL' : 'en-GB';
  const nowDate = new Date();
  const now = nowDate.getTime();
  const todayKey = dayKey(nowDate);

  const items: DueItem[] = [];
  for (const course of selectedCourses) {
    const all = (exercises.data?.exercises ?? []).filter((e) => e.courseRef === course.moodleCourseId);
    const sheets = all.filter((e) => new Date(e.dueAt).getTime() >= now);
    for (const e of sheets) {
      items.push({
        key: `hw:${course.moodleCourseId}:${e.exerciseId}`,
        sheetId: `${course.moodleCourseId}/${e.exerciseId}`,
        course,
        title: e.title,
        kind: 'homework',
        day: dayKey(new Date(e.dueAt)),
        at: new Date(e.dueAt).getTime(),
        dueAt: e.dueAt,
        stateText: labels.exerciseState(e.state, e.solutionOpen),
        state: e.state,
        step: STEP_OF[e.state],
        todo: e.state === 'not_posted' || e.state === 'posted',
      });
    }
    if (all.length === 0 && course.nextDue !== null && new Date(course.nextDue.dueAt).getTime() >= now) {
      items.push({
        key: `hw:${course.moodleCourseId}:next-due`,
        course,
        title: course.nextDue.exerciseTitle,
        kind: 'homework',
        day: dayKey(new Date(course.nextDue.dueAt)),
        at: new Date(course.nextDue.dueAt).getTime(),
        dueAt: course.nextDue.dueAt,
        todo: true,
      });
    }
    for (const a of assessments.data?.assessments ?? []) {
      if (a.courseRef !== course.moodleCourseId || a.date < todayKey) continue;
      items.push({
        key: `as:${a.assessmentId}`,
        course,
        title: a.title,
        kind: 'assessment',
        day: a.date,
        at: startOfDay(a.date).getTime(),
        date: a.date,
        kindLabel: kinds.kind(a.kind),
        todo: true,
      });
    }
  }
  items.sort((x, y) => x.at - y.at);

  // The one next step: the earliest homework still to do; else the earliest assessment; else nothing.
  const nextStep = items.find((i) => i.kind === 'homework' && i.todo) ?? items.find((i) => i.kind === 'assessment');

  const today = startOfDay(todayKey);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i, 12);
    return { key: dayKey(date), date };
  });
  const weekdayShort = new Intl.DateTimeFormat(intl, { weekday: 'short' });
  const longDate = new Intl.DateTimeFormat(intl, { weekday: 'long', day: 'numeric', month: 'long' });
  const dayNumber = new Intl.DateTimeFormat(intl, { day: 'numeric' });
  const itemText = (i: DueItem): string => `${courseShortName(i.course, locale)} · ${i.title}`;
  const pickedDay = weekDays[picked] ?? weekDays[0]!;
  const pickedItems = items.filter((i) => i.day === pickedDay.key);

  const stepNames = [t.stepPosted, t.stepChecked, t.stepFixed];
  const weekRange = new Intl.DateTimeFormat(intl, { day: 'numeric', month: 'long' }).formatRange(
    weekDays[0]!.date,
    weekDays[6]!.date,
  );
  const clock = new Intl.DateTimeFormat(intl, { hour: '2-digit', minute: '2-digit', hour12: false });
  // The list is "the next two weeks"; the next step above looks further, so a far-off sheet can still be the one to do.
  const horizon = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14).getTime();
  const dueRows = items.filter((i) => i.at < horizon);
  const tomorrowKey = dayKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  // "Today" or "tomorrow" only when the due day really is; any other day is named by its date alone.
  const dueWord = (dueAt: string): 'today' | 'tomorrow' | null => {
    const key = dayKey(new Date(dueAt));
    return key === todayKey ? 'today' : key === tomorrowKey ? 'tomorrow' : null;
  };
  const dueLine = (i: DueItem): string =>
    i.kind === 'homework' && i.dueAt !== undefined
      ? `${labels.dueOn(labels.dueDate(i.dueAt))}, ${clock.format(new Date(i.dueAt))}`
      : `${t.assessmentWord} · ${i.kindLabel ?? ''} · ${assessmentDate(i.date ?? '', locale)}`;
  // Label and style follow the row's real state; the link is the real route for it. A sheet's feedback lives under
  // its upload, which the exercise record does not carry, so Feedback goes to the homework screen that lists it.
  const actionOf = (i: DueItem): { readonly label: string; readonly to: string; readonly cls: string } => {
    if (i.kind === 'assessment') return { label: t.actionPrepare, to: '/exam-prep', cls: 'home-action-tint' };
    if (i.state === 'not_posted') return { label: t.actionPost, to: '/homework', cls: 'home-action-filled' };
    if (i.state === 'checked') return { label: t.actionFeedback, to: '/homework', cls: 'home-action-tint' };
    return { label: t.actionOpen, to: '/homework', cls: 'home-action-neutral' };
  };

  return (
    <div className="page home">
      <h1 className="visually-hidden">{t.heading}</h1>

      {moodle.data?.connected === false ? (
        <p className="home-note">
          {t.moodleNudge}. <Link to="/welcome">{t.moodleNudgeLink}</Link>
        </p>
      ) : null}

      {exercises.isPending || selectedCourses.length === 0 ? null : (
        <>
          <div className="home-top">
            <section className="home-next" aria-labelledby="home-next-title" data-course={nextStep?.course.colour}>
              <div className="home-next-head">
                <span className="home-next-icon" aria-hidden="true">
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {nextStep?.kind === 'assessment' ? (
                      <path d="M8 3v4M16 3v4M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
                    ) : (
                      <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
                    )}
                  </svg>
                </span>
                <div className="home-next-body">
                  <h2 id="home-next-title" className="home-next-label">
                    {t.nextStep}
                  </h2>
                  {nextStep === undefined ? (
                    <p className="home-next-sub">{t.nothingDueAll}</p>
                  ) : (
                    <>
                      <p className="home-next-title">
                        {nextStep.kind === 'homework' && nextStep.state === 'not_posted' ? `${t.postVerb} ` : null}
                        <bdi dir="auto">{nextStep.title}</bdi>
                      </p>
                      <p className="home-next-sub">
                        <bdi dir="auto">{courseShortName(nextStep.course, locale)}</bdi> ·{' '}
                        {nextStep.kind === 'homework' && nextStep.dueAt !== undefined
                          ? t.dueWhen(dueWord(nextStep.dueAt), labels.dueDate(nextStep.dueAt), clock.format(new Date(nextStep.dueAt)))
                          : assessmentDate(nextStep.date ?? '', locale)}
                      </p>
                    </>
                  )}
                </div>
              </div>
              {nextStep === undefined ? null : nextStep.kind === 'homework' ? (
                <div className="home-next-actions">
                  <Link className="home-cta" to="/homework" onClick={() => setFocus(nextStep.course.moodleCourseId)}>
                    {nextStep.state === 'not_posted' ? t.postIt : t.toHomework}
                  </Link>
                </div>
              ) : (
                // An assessment's natural home is exam preparation; it takes no course focus.
                <div className="home-next-actions">
                  <Link className="home-cta" to="/exam-prep">
                    {t.toPrep}
                  </Link>
                </div>
              )}
            </section>

            <section className="home-week" aria-labelledby="home-week-title">
              <div className="home-week-head">
                <h2 id="home-week-title" className="home-section-title">
                  {t.thisWeek}
                </h2>
                <span className="home-week-range" dir="ltr">
                  {weekRange}
                </span>
              </div>
              <ol className="plain-list home-days">
                {weekDays.map((d, index) => {
                  const due = items.filter((i) => i.day === d.key);
                  const aria = `${longDate.format(d.date)}: ${due.length === 0 ? t.nothingThisDay : due.map(itemText).join(', ')}`;
                  return (
                    <li key={d.key}>
                      <button
                        type="button"
                        className="home-day"
                        aria-pressed={index === picked}
                        aria-label={aria}
                        data-today={index === 0 ? '' : undefined}
                        onClick={() => setPicked(index)}
                      >
                        <span className="home-day-name">{weekdayShort.format(d.date)}</span>
                        <span className="home-day-num">{dayNumber.format(d.date)}</span>
                        <span className="home-day-marks" aria-hidden="true">
                          {due.map((i) => (
                            <span key={i.key} className="home-mark" data-course={i.course.colour}>
                              <CourseSymbol symbol={i.course.symbol} />
                            </span>
                          ))}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <div className="home-day-detail" aria-live="polite">
                <strong>{longDate.format(pickedDay.date)}</strong>
                {pickedItems.length === 0 ? (
                  <span className="home-day-empty">{t.nothingThisDay}</span>
                ) : (
                  pickedItems.map((i) => (
                    <span key={i.key} className="home-day-item" data-course={i.course.colour}>
                      <span className="home-day-dot" aria-hidden="true" />
                      <bdi dir="auto">{itemText(i)}</bdi>
                    </span>
                  ))
                )}
              </div>
            </section>
          </div>

          {dueRows.length === 0 ? null : (
            <section className="home-due" aria-labelledby="home-due-title">
              <div className="home-due-head">
                <h2 id="home-due-title" className="home-section-title">
                  {t.dueSoon}
                </h2>
                <span className="home-legend" role="img" aria-label={t.legendAria}>
                  {stepNames.map((name) => (
                    <span key={name} className="home-legend-item">
                      <span className="home-legend-dot" />
                      {name}
                    </span>
                  ))}
                </span>
              </div>
              <ul className="plain-list home-due-list">
                {dueRows.map((i) => {
                  const when = new Date(i.at);
                  const action = actionOf(i);
                  return (
                    <li key={i.key} className="home-due-row" data-course={i.course.colour}>
                      <span className="home-due-day">
                        <span className="home-day-name">{weekdayShort.format(when)}</span>
                        <strong dir="ltr">{dayNumber.format(when)}</strong>
                      </span>
                      <span className="home-tile">
                        <CourseSymbol symbol={i.course.symbol} />
                      </span>
                      <span className="home-due-text" style={i.sheetId === undefined ? undefined : morphName('sheet', i.sheetId)}>
                        <strong>
                          <bdi dir="auto">{itemText(i)}</bdi>
                        </strong>
                        <span className="home-due-sub">
                          {dueLine(i)}
                          {i.stateText === undefined ? null : ` · ${i.stateText}`}
                        </span>
                      </span>
                      {i.step === undefined ? null : (
                        <span className="home-stepper" aria-hidden="true">
                          {stepNames.map((name, n) => (
                            <span key={name} className="home-step" data-reached={n < (i.step ?? 0) ? '' : undefined} />
                          ))}
                        </span>
                      )}
                      <Link
                        to={action.to}
                        className={`home-action ${action.cls}`}
                        onClick={i.kind === 'homework' ? () => setFocus(i.course.moodleCourseId) : undefined}
                      >
                        {action.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

        </>
      )}

      <div className="home-foot">
        {exercises.isPending || selectedCourses.length === 0 ? null : (
          <p className="home-add">
            <Link to="/settings/courses#assessments">{t.addAssessment}</Link>
          </p>
        )}
        <UsageMeter />
      </div>
    </div>
  );
}
